import { useEffect, useRef, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { ChevronGrabberVertical, DatePickerIcon } from "@acto/web-common/components";
import { useMeasuredSize } from "@acto/taskpane/field-sales-2/utils/hooks/useMeasuredSize";
import { useTwoPhaseMorph } from "./useTwoPhaseMorph";

const MORPH_MS = 180;
const MORPH_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const PILL_HEIGHT = 48;
const MIN_PANEL_WIDTH = 200;
const PILL_RADIUS = 99;
const PANEL_RADIUS = 28;

// In-flow placeholder: reserves the collapsed pill's footprint so the bottom-nav tab row never
// shifts when the panel expands (the MorphBox overlays upward via absolute positioning).
const Anchor = styled("div")({
  position: "relative",
  height: PILL_HEIGHT,
  maxWidth: "80vw",
});

// The single morphing surface. Collapsed it is the pill; expanded it is the options panel. Width
// and height are driven inline; the transition string flips per phase so width leads on open and
// height leads on close.
const MorphBox = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: "50%",
  transform: "translateX(-50%)",
  overflow: "hidden",
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  // content-box so the inline width/height equal the measured *content* size — a border-box
  // morph box would subtract its 1px border and clip the pill label (the "6 Monate" → "6…" bug).
  boxSizing: "content-box",
  maxWidth: "80vw",
}));

const PillButton = styled("button")(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  height: PILL_HEIGHT,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
  color: theme.fieldSalesTwo.colors.neutral[900],
  outline: "none",
  whiteSpace: "nowrap",
  transition: `opacity ${MORPH_MS}ms ${MORPH_EASE}`,
  // Inset (not outline) so the keyboard focus ring isn't clipped by the morph box's overflow:hidden.
  "&:focus-visible": {
    boxShadow: `inset 0 0 0 ${theme.strokeWidth.regular} ${theme.fieldSalesTwo.colors.neutral[300]}`,
  },
}));

const PillLabel = styled("span")({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const TriggerChevron = styled(ChevronGrabberVertical)(({ theme }) => ({
  width: 16,
  height: 16,
  flexShrink: 0,
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const Panel = styled("div")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  padding: theme.fieldSalesTwo.spacing[8],
  gap: theme.fieldSalesTwo.spacing[2],
  transition: `opacity ${MORPH_MS}ms ${MORPH_EASE}`,
}));

// Off-screen clones used only to measure the natural collapsed (pill) and expanded (panel) sizes.
const Measurer = styled("div")({
  position: "absolute",
  left: 0,
  top: 0,
  visibility: "hidden",
  pointerEvents: "none",
  zIndex: -1,
  whiteSpace: "nowrap",
});

export interface MorphPillProps {
  /** Whether the panel is expanded. The pill toggles this; outside-click / Escape collapse it. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Collapsed-pill label (e.g. "3M" or a formatted date range). */
  label: ReactNode;
  /** Accessible label for the trigger button. */
  ariaLabel: string;
  /** Panel contents shown when expanded (rendered live and in the hidden size measurer). */
  children: ReactNode;
}

/**
 * The animated period pill: a single surface that morphs between the collapsed pill (icon +
 * label + chevron) and the expanded options panel. Owns the two-phase morph (width-then-height),
 * the off-screen size measurement, and outside-click / Escape collapse. The picker logic that
 * fills the panel is passed in as `children`.
 */
export function MorphPill({ open, onOpenChange, label, ariaLabel, children }: MorphPillProps) {
  const morphRef = useRef<HTMLDivElement | null>(null);
  const [pillMeasureRef, pillSize] = useMeasuredSize();
  const [panelMeasureRef, panelSize] = useMeasuredSize();
  const { wide, tall, onTransitionEnd } = useTwoPhaseMorph(open, morphRef, MORPH_MS);

  // Outside-click + Escape collapse (no scrim).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (morphRef.current && !morphRef.current.contains(event.target as Node)) onOpenChange(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange]);

  const collapsedWidth = pillSize.width || undefined;
  const expandedWidth = Math.max(panelSize.width, pillSize.width, MIN_PANEL_WIDTH);
  const morphWidth = wide ? expandedWidth : collapsedWidth;
  const morphHeight = tall ? panelSize.height : PILL_HEIGHT;
  // No per-property delays: the phase booleans (wide → tall on open, tall → wide on close) are
  // chained by the morph box's transitionend, so each phase animates only once its predecessor ends.
  const morphTransition = `width ${MORPH_MS}ms ${MORPH_EASE}, height ${MORPH_MS}ms ${MORPH_EASE}, border-radius ${MORPH_MS}ms ${MORPH_EASE}`;

  return (
    <Anchor style={{ width: collapsedWidth }}>
      <MorphBox
        ref={morphRef}
        onTransitionEnd={onTransitionEnd}
        style={{
          width: morphWidth,
          height: morphHeight,
          borderRadius: tall ? PANEL_RADIUS : PILL_RADIUS,
          transition: morphTransition,
        }}
      >
        <PillButton
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          onClick={() => onOpenChange(!open)}
          style={{ opacity: tall ? 0 : 1, pointerEvents: tall ? "none" : "auto" }}
        >
          <DatePickerIcon />
          <PillLabel>{label}</PillLabel>
          <TriggerChevron />
        </PillButton>

        <Panel role="listbox" style={{ opacity: tall ? 1 : 0, pointerEvents: tall ? "auto" : "none" }}>
          {children}
        </Panel>
      </MorphBox>

      {/* Hidden measurers — natural collapsed (pill) and expanded (panel) sizes. */}
      <Measurer ref={pillMeasureRef}>
        <PillButton as="div" style={{ position: "static", display: "inline-flex" }}>
          <DatePickerIcon />
          <PillLabel>{label}</PillLabel>
          <TriggerChevron />
        </PillButton>
      </Measurer>
      <Measurer ref={panelMeasureRef}>
        <Panel as="div" style={{ position: "static", display: "inline-flex" }}>
          {children}
        </Panel>
      </Measurer>
    </Anchor>
  );
}
